import {
    type Message,
    type AgentMessageFragment,
    type UserMessage,
    type AgentMessage,
    isUserMessage,
    isAgentResponse,
    type AgentMessageFragmentTodoItem,
} from "@/hooks/use-stream-conversation";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";

export type GroupedMessage = UserMessage | AgentMessage | AgentTodoGroup;

export type AgentTodoGroup = {
    type: "todo-group";
    groupId: string;
    todos: AgentTodoGroupItem[];
};

export type AgentTodoGroupItem = {
    todoId: string;
    todoTitle: string;
    todoStatus: AgentResponseBlock_TodoItem_Phase;
    fragments: AgentMessageFragment[];
};

/**
 * Groups messages by todo items
 *
 * Todos are grouped based on when they were created together.
 * The agent typically creates multiple todos at once (via todo_reset or todo_write),
 * and clears them together (via todo_clear or todo_reset).
 *
 * A TODO group never contains user messages. Agent messages that aren't part of a
 * group are returned as a stand-alone agent message with an updated ID.
 *
 * @param messages - The messages to group
 */
export function groupMessagesByTodo(messages: Message[]): GroupedMessage[] {
    const results: GroupedMessage[] = [];
    let groupIndex = 0;

    for (const message of messages) {
        if (isUserMessage(message)) {
            results.push(message);
            continue;
        }

        if (isAgentResponse(message)) {
            const grouped = groupAgentMessagesByTodo(message, groupIndex);
            results.push(...grouped.results);
            groupIndex = grouped.nextGroupIndex;
        }
    }
    return results;
}

function groupAgentMessagesByTodo(
    message: AgentMessage,
    groupIndex: number,
): { results: (AgentMessage | AgentTodoGroup)[]; nextGroupIndex: number } {
    const results: (AgentMessage | AgentTodoGroup)[] = [];

    let current: AgentMessage | AgentTodoGroup | null = null;
    let nextAgentMessageId = message.id;
    let nextGroupIndex = groupIndex;
    for (const fragment of message.fragments) {
        // For non-todo fragments we just want to append it to the current message
        if (fragment.type !== "todoItem") {
            if (current === null) {
                current = initializeAgentMessage(fragment, nextAgentMessageId);
                nextAgentMessageId += 1;
            } else if (current.type === "agent") {
                current.fragments.push(fragment);
            } else if (current.type === "todo-group") {
                const inProgress = current.todos.find(
                    (t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS,
                );
                // For todo-groups we either add it to the todo that's in-progress
                if (inProgress) {
                    inProgress.fragments.push(fragment);
                } else {
                    // or if there are no in-progress todos, we end the todo group and
                    // start an agent message instead.
                    results.push(current);
                    current = initializeAgentMessage(fragment, nextAgentMessageId);
                    nextAgentMessageId += 1;
                }
            }
            continue;
        }

        // For todo fragments we either update the existing group or create a new group
        if (current?.type === "todo-group") {
            updateTodos(current.todos, fragment);
            continue;
        } else {
            // If we see a pending|in-progress todo and we aren't already in a todo group,
            // then we start a new group.
            if (
                fragment.status === AgentResponseBlock_TodoItem_Phase.PENDING ||
                fragment.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS
            ) {
                if (current) results.push(current);
                current = {
                    type: "todo-group",
                    groupId: `todo-group-${nextGroupIndex}`,
                    todos: [initializeTodoGroupItem(fragment)],
                };
                nextGroupIndex += 1;
                continue;
            }

            // Otherwise se just add the fragment to the current message
            if (current) {
                current.fragments.push(fragment);
            }
        }
    }

    if (current) {
        results.push(current);
    }

    return { results, nextGroupIndex };
}

/** Updates the todos so they reflects the state of the latest todo represented by the fragment */
function updateTodos(todos: AgentTodoGroupItem[], fragment: AgentMessageFragmentTodoItem): void {
    for (const todo of todos) {
        if (todo.todoId === fragment.todoItemId) {
            // Don't update the status if it was already done (i.e. don't clear it)
            todo.todoStatus =
                todo.todoStatus !== AgentResponseBlock_TodoItem_Phase.DONE ? fragment.status : todo.todoStatus;
            todo.todoTitle = fragment.title;
            return;
        }
    }
    todos.push(initializeTodoGroupItem(fragment));
}

function initializeTodoGroupItem(fragment: AgentMessageFragmentTodoItem): AgentTodoGroupItem {
    return {
        todoId: fragment.todoItemId,
        todoStatus: fragment.status,
        todoTitle: fragment.title,
        fragments: [],
    };
}

function initializeAgentMessage(fragment: AgentMessageFragment, nextAgentMessageId: number): AgentMessage {
    return {
        type: "agent",
        id: nextAgentMessageId,
        fragments: [fragment],
    };
}

export function calculateTodoGroupProgress(todos: AgentTodoGroupItem[]): { started: number; total: number } {
    const total = todos.length;
    const pending = todos.filter((t) => t.todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING).length;
    return { started: total - pending, total };
}
